import { useState, useRef, useEffect} from 'react';
import {auth, firestore} from './firebaseConfig';
import {getLatestSession, getAllSessions} from './attendee_actions';
import * as Graph from './graph_display';

//checks window size
export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;

    function handleResize() {
      if(mountedRef.current) {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }
    }

    window.addEventListener("resize", handleResize);
    handleResize();
    
    return () => {
      window.removeEventListener("resize", handleResize);
      mountedRef.current = false;
    }
  }, []); 
  return windowSize;
}

//Checks if the session chat is on
export const useChatOn = (sessionId) => {
    const [chatOn,setChat] = useState(0);
    const mountedRef = useRef(false);
  
    useEffect(() => {
      mountedRef.current = true;
      if(sessionId){
        const unsubscribe = firestore
            .collection('meetings').doc(sessionId)
            .onSnapshot((doc) => {
                if(mountedRef.current) setChat(doc.data()?.chatOn);
            });
        return () => {
          unsubscribe();  
          mountedRef.current = false; 
        } 
      } 
    }, [sessionId]) 

    return chatOn
}

//Checks if the session graph is on
export const useGraphOn = (sessionId) => {
  const [graphOn,setGraph] = useState(false);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    if(sessionId){
      const unsubscribe = firestore
          .collection('meetings').doc(sessionId)
          .onSnapshot((doc) => {
            if(mountedRef.current) setGraph(doc.data()?.graphOn);
          });
      return () => {
        unsubscribe(); 
        mountedRef.current = false;
      }  
    } 
  }, [sessionId]) 
  
  return graphOn;
}

export const useAuthState = () => {
    const [user, setUser] = useState(null);
    const mountedRef = useRef(false);

    useEffect(() => {
      mountedRef.current = true;

      async function checkFlaggedAndSetUser (userAuth) {
        const flaggedUser = await firestore.collection("flaggedUsers").doc(userAuth.uid).get();
        if(mountedRef.current) {
          setUser({
            uid: userAuth.uid,
            email: null,
            isAnonymous: true,
            flagged: flaggedUser.exists
          })
        }
      }

      const unsubscribe = auth.onAuthStateChanged(userAuth => {
        //console.log(`my id is ${userAuth?.uid}`)
        //localStorage.removeItem("currentSession");
        if (userAuth) {
          if(userAuth.isAnonymous) checkFlaggedAndSetUser(userAuth)
          else {
            if(mountedRef.current) {
              setUser({
              uid: userAuth.uid,
              email: userAuth?.email,
              isAnonymous: userAuth?.isAnonymous,
              flagged: false
              })
            }
          }
        } else {
          if(mountedRef.current) setUser(null);
        }

      })

      return () => {
        unsubscribe();
        mountedRef.current = false;
      }
    }, [])
  
    return user;
  }

//fetches current session
export const useSessionId = () => {
  const [sessionId, setSessionId] = useState(null);
  const mountedRef = useRef(false);

  useEffect(() => { 
    mountedRef.current = true;

    async function fetchSessionID() {
      try{
        getLatestSession().then(ref => {if(mountedRef.current) setSessionId(ref.id)});
      }
      catch (e){
        console.error(e);
      }
    };

    fetchSessionID();

    return () => { mountedRef.current = false;}
  }, []); 

  //console.log(`SessionId is: ${sessionId}`);
  return sessionId;
}

//fetches meetings of a user
export const useUserMeetings = (userId) => {
  const [meetings,setMeetings] = useState(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    if(userId){
      async function fetchUserMeetings(){

      const snapshot = await firestore.collection("organizerSessions").where("organizerId", "==", userId).get();
      const sessions = [];

      for(const doc of snapshot.docs){
        const sessionInfo = await Graph.getSessionInfo(doc.data().sessionId);
        if(sessionInfo.get("name")) {
          sessionInfo.set("sessionId", doc.data().sessionId);
          sessions.push(sessionInfo);
        }
      }

      sessions.sort((a,b) => {
        return (b.get("date") === a.get("date"))? 
        b.get("time").localeCompare(a.get("time"))
        :
        b.get("date").localeCompare(a.get("date"))
        })
      if(mountedRef.current) setMeetings(sessions);
      }

     
      const unsubscribe = firestore
        .collection('organizerSessions').where("organizerId", "==", userId)
        .onSnapshot((doc) => {
          fetchUserMeetings();
        });
      return () => {
        unsubscribe(); 
        mountedRef.current = false;
      }    
    }
    
  }, [userId]) 

  return meetings;
}

//fetches all meetings 
export const useAllMeetings = () => {
  const [meetings,setMeetings] = useState([]);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    
    async function fetchMeetings() {
      try{
        await getAllSessions().then((ref) => {
          const sessions = ref.map((session) => ({
            id: session.id,
            name: session.name,
            date: session.date,
            time: session.time
          }));
          if(mountedRef.current) setMeetings(sessions); //prevents memory leak
        });
      }
      catch (e){
        console.error(e);
      }
    }; 
    fetchMeetings()

    return () => {mountedRef.current = false}
  }, []) 

  return meetings;
}

export const useSessionReactions = (sessionId, admin) => {
  const [sessionReactions, setSessionReactions] = useState([]);
  const mountedRef = useRef(false);

  useEffect(() => { 
      mountedRef.current = true;

      async function fetchAttendeeReactions() {
        if(sessionId) {
          const reactions = await Graph.getReactions(sessionId);
          if(mountedRef.current) setSessionReactions(reactions);
        }
      }

      async function fetchAdminReactions() {
        if(sessionId) {
          const reactions = await Graph.getTopics(sessionId);
          if(mountedRef.current) setSessionReactions(reactions);
        }
      }

      if(admin) fetchAdminReactions();
      else fetchAttendeeReactions();
      
    return () => {mountedRef.current = false}
  }, [sessionId, admin]);

  //console.log(`SessionReactions are: ${sessionReactions?.toString()}`);
  return sessionReactions;
}

export const useSessionColors = (sessionId, admin) => {
  const defaultColors = ["#E41A1C", "#FF7F00", "#377EB8", "#984EA3", "#4DAF4A", "#404142"];
  const [sessionColors, setSessionColors] = useState(defaultColors);
  const mountedRef = useRef(false);

  useEffect(() => { 
      mountedRef.current = true;

      async function fetchAttendeeReactions() {
        if(sessionId) {
          const reactions = await Graph.getReactionColors(sessionId);
          if(mountedRef.current && reactions && reactions.length > 0) setSessionColors(reactions);
        }
      }

      async function fetchAdminReactions() {
        //need to add function
        /*if(sessionId) {
          const reactions = await Graph.getTopics(sessionId);
          if(mountedRef.current) setSessionColors(reactions);
        }*/
      }

      if(admin) fetchAdminReactions();
      else fetchAttendeeReactions();
      
    return () => {mountedRef.current = false}
  }, [sessionId, admin]);

  //console.log(`sessionColors are: ${sessionColors?.toString()}`);
  return sessionColors;
}

//fetches session info
export const useSessionInfo = (sessionId) => {
  const [sessionInfo, setSessionInfo] = useState(new Map([
    ["name", ""],
    ["date", ""],
    ["time", ""]
  ]));
  const mountedRef = useRef(false);

  useEffect(() => { 
    mountedRef.current = true;

    async function fetchSessionInfo() {
      try{
        if(sessionId) Graph.getSessionInfo(sessionId).then(ref => {if(mountedRef.current) setSessionInfo(ref)});
      }
      catch (e){
        console.error(e);
      }
    };

    fetchSessionInfo();
    return () => {mountedRef.current = false;}
  }, [sessionId]); 

  return sessionInfo;
}

//Checks if the session is still active
export const useSessionState = (sessionId) => {
  const [activeSession, setActiveSession] =  useState(true);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    if(sessionId){
      const unsubscribe = firestore
          .collection('meetings').doc(sessionId)
          .onSnapshot((doc) => {
              if(mountedRef.current && doc.data()?.endTime) setActiveSession(false);
              else setActiveSession(true);
          });
      return () => {
        unsubscribe();  
        mountedRef.current = false; 
      } 
    } 
  }, [sessionId]) 
  
  return activeSession;
}